<template>
    <a v-if="link" :href="link" :target="linkTarget"
       class="cr:font-semibold cr:text-base cr:transition cr:duration-300 cr:text-center cr:cursor-pointer cr:items-center cr:inline-flex cr:justify-center cr:px-6 cr:py-2"
       :class="[
                small ? 'cr:h-9' : 'cr:h-11',
                color === 'cyan' ? 'cr:bg-cyan-500 cr:hover:bg-cyan-600 cr:text-white' : '',
                color === 'cyan-outline' ? 'cr:text-cyan-500 cr:border cr:border-cyan-500 cr:bg-transparent cr:hover:bg-cyan-100 cr:hover:bg-opacity-50' : '',
                color === 'green' ? 'cr:bg-emerald-500 cr:hover:bg-emerald-600 cr:text-white' : '',
                color === 'green-outline' ? 'cr:text-emerald-500 cr:border cr:border-emerald-500 cr:bg-transparent cr:hover:bg-emerald-100 cr:hover:bg-opacity-50' : '',
                color === 'red' ? 'cr:bg-rose-500 cr:hover:bg-rose-600 cr:text-white' : '',
                color === 'red-outline' ? 'cr:text-rose-500 cr:border cr:border-rose-500 cr:bg-transparent cr:hover:bg-rose-100 cr:hover:bg-opacity-50' : '',
                color === 'orange' ? 'cr:bg-orange-500 cr:hover:bg-orange-600 cr:text-white' : '',
                color === 'orange-outline' ? 'cr:text-orange-500 cr:border cr:border-orange-500 cr:bg-transparent cr:hover:bg-orange-100 cr:hover:bg-opacity-50' : '',
                color === 'slate' ? 'cr:bg-slate-600 cr:hover:bg-slate-700 cr:text-white' : '',
                color === 'slate-outline' ? 'cr:text-slate-500 cr:border cr:border-slate-500 cr:bg-transparent cr:hover:bg-slate-500 cr:hover:bg-opacity-25' : '',
                color === 'slate-light' ? 'cr:bg-slate-400 cr:hover:bg-slate-500 cr:text-white' : '',
                color === 'white' ? 'cr:bg-white cr:text-cyan-500' : '',
                widthClass]">
        <span v-if="icon">
            <slot name="icon">

            </slot>
        </span>
        <slot></slot>
    </a>
    <button v-else
            class="cr:font-semibold cr:text-base cr:transition-all cr:duration-300 cr:text-center cr:cursor-pointer cr:items-center cr:inline-flex cr:justify-center cr:px-6 cr:py-2"
            :class="[
                small ? 'cr:h-9' : 'cr:h-11',
                color === 'cyan' ? 'cr:bg-cyan-500 cr:hover:bg-cyan-600 cr:text-white' : '',
                color === 'cyan-outline' ? 'cr:text-cyan-500 cr:border-2 cr:border-cyan-500 cr:bg-transparent cr:hover:bg-cyan-100 cr:hover:bg-opacity-50' : '',
                color === 'green' ? 'cr:bg-emerald-500 cr:hover:bg-emerald-600 cr:text-white' : '',
                color === 'green-outline' ? 'cr:text-emerald-500 cr:border-2 cr:border-emerald-500 cr:bg-transparent cr:hover:bg-emerald-100 cr:hover:bg-opacity-50' : '',
                color === 'red' ? 'cr:bg-rose-500 cr:hover:bg-rose-600 cr:text-white' : '',
                color === 'red-outline' ? 'cr:text-rose-500 cr:border-2 cr:border-rose-500 cr:bg-transparent cr:hover:bg-rose-100 cr:hover:bg-opacity-50' : '',
                color === 'orange' ? 'cr:bg-orange-500 cr:hover:bg-orange-600 cr:text-white' : '',
                color === 'orange-outline' ? 'cr:text-orange-500 cr:border-2 cr:border-orange-500 cr:bg-transparent cr:hover:bg-orange-100 cr:hover:bg-opacity-50' : '',
                color === 'slate' ? 'cr:bg-slate-600 cr:hover:bg-slate-700 cr:text-white' : '',
                color === 'slate-outline' ? 'cr:text-slate-500 cr:border-1 cr:border-slate-500 cr:bg-transparent': '',
                color === 'slate-light' ? 'cr:bg-slate-400 cr:hover:bg-slate-500 cr:text-white' : '',
                color === 'white' ? 'cr:bg-white cr:text-cyan-500' : '',
                color === 'origin' ? originColor : '',
                disableButton ? 'cr:grayscale-[80%] cr:opacity-25 cr:pointer-events-none' : '',
                widthClass]">
        <span v-if="icon">
            <slot name="icon">
            </slot>
        </span>
        <span v-if="label">
            {{ label }}
        </span>
        <slot></slot>
    </button>
</template>

<script setup lang="ts">
import {computed} from "vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import {useRegistrationV4Store} from "@/stores/registrationV4.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

const registrationStore = useRegistrationStore();
const registrationV4Store = useRegistrationV4Store();

// Use whichever store has a recognised origin — V3 sets registrationOrigin, V4 sets origin
const activeOrigin = computed(() =>
    registrationStore.registrationOrigin || registrationV4Store.origin || null
);

const props = defineProps({
    color: {
        type: String,
        default: 'primary',
    },
    icon: {
        type: Boolean,
        default: false,
    },
    small: {
        type: Boolean,
        default: false,
    },
    link: {
        type: String,
        default: '',
    },
    linkTarget: {
        type: String,
        default: '',
    },
    label: {
        type: String,
        default: null,
    },
    disableButton: {
        type: Boolean,
        default: false,
    },
    widthClass: {
        type: String,
        default: ''
    }
})

const originColor = computed(() => {
    switch (activeOrigin.value?.toLocaleLowerCase()) {
        case ORIGINS.BE:
            return 'cr:bg-[var(--be-primary)] cr:hover:bg-[var(--be-hover)] cr:rounded-lg';
        case ORIGINS.KE:
            return 'cr:bg-[var(--ke-primary)] cr:hover:bg-[var(--ke-hover)] cr:rounded-lg';
        case ORIGINS.FIXR:
            return 'cr:bg-[var(--fixr-primary)] cr:hover:bg-[var(--fixr-hover)] cr:text-white cr:rounded-lg';
        case ORIGINS.SR:
            return 'cr:bg-[var(--sr-primary)] cr:hover:bg-[var(--sr-hover)] cr:text-white cr:rounded-lg';
        case ORIGINS.RC:
            return 'cr:bg-[var(--rc-primary)] cr:hover:bg-[var(--rc-hover)] cr:text-white cr:rounded-lg';
        case ORIGINS.HM:
            return 'cr:bg-[var(--hm-primary)] cr:hover:bg-[var(--hm-hover)] cr:text-white cr:rounded-full cr:hover:text-[var(--hm-primary-3)]]';
        default:
            return 'cr:bg-[var(--default-primary)] cr:hover:bg-[var(--default-hover)] cr:text-white cr:rounded-lg';
    }
})

</script>

<style scoped>

</style>
